import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import './Absencje.css';

function Absencje({ users, projects, currentUser }) {
  const [selectedProject, setSelectedProject] = useState(projects.length > 0 ? projects[0] : '');
  const [selectedRotation, setSelectedRotation] = useState('all'); // 'all' lub ID rotacji
  const [viewMode, setViewMode] = useState('month'); // 'day', 'week', 'month', 'quarter', 'year', 'custom'
  const [dateFrom, setDateFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1)); // Początek bieżącego miesiąca
  const [dateTo, setDateTo] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)); // Koniec bieżącego miesiąca
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWeek, setSelectedWeek] = useState(null); // Numer tygodnia w roku
  const [selectedQuarter, setSelectedQuarter] = useState(Math.floor(new Date().getMonth() / 3) + 1); // 1-4
  const [scheduleData, setScheduleData] = useState({});
  const [rotations, setRotations] = useState([]);

  // Typy dni wolnych/absencyjnych zgodnych z polskim prawem pracy
  const absenceTypes = {
    'urlop': { label: 'Urlop', color: '#3b82f6', icon: '🏖️' },
    'urlop_na_zadanie': { label: 'Urlop na żądanie', color: '#60a5fa', icon: '🏝️' },
    'chorobowe': { label: 'Zwolnienie lekarskie', color: '#f59e0b', icon: '🏥' },
    'opieka': { label: 'Opieka nad dzieckiem', color: '#ec4899', icon: '👶' },
    'okolicznosciowy': { label: 'Urlop okolicznościowy', color: '#8b5cf6', icon: '🎉' },
    'bezplatny': { label: 'Urlop bezpłatny', color: '#6b7280', icon: '📅' },
    'swieto': { label: 'Święto', color: '#ef4444', icon: '🎊' },
    'niedziela': { label: 'Niedziela', color: '#9ca3af', icon: '⛪' }
  };

  // Nazwy miesięcy po polsku
  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ];

  // Funkcja do obliczenia numeru tygodnia w roku (ISO 8601)
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Funkcja do obliczenia daty początku tygodnia na podstawie numeru tygodnia i roku
  const getDateFromWeek = (weekNumber, year) => {
    const simple = new Date(year, 0, 1 + (weekNumber - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) {
      ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    } else {
      ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    }
    return ISOweekStart;
  };

  // Oblicz daty dla wybranego zakresu
  const getDateRange = () => {
    switch (viewMode) {
      case 'day':
        return {
          start: new Date(dateFrom),
          end: new Date(dateFrom),
          days: 1
        };
      case 'week':
        if (selectedWeek !== null && selectedWeek !== undefined) {
          const weekStart = getDateFromWeek(selectedWeek, selectedYear);
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          return {
            start: weekStart,
            end: weekEnd,
            days: 7
          };
        }
        // Fallback - użyj dateFrom
        const weekStart = new Date(dateFrom);
        weekStart.setDate(dateFrom.getDate() - weekStart.getDay() + 1); // Poniedziałek
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return {
          start: weekStart,
          end: weekEnd,
          days: 7
        };
      case 'month':
        const monthStart = new Date(selectedYear, selectedMonth, 1);
        const monthEnd = new Date(selectedYear, selectedMonth + 1, 0);
        return {
          start: monthStart,
          end: monthEnd,
          days: monthEnd.getDate()
        };
      case 'quarter':
        const quarterStart = new Date(selectedYear, (selectedQuarter - 1) * 3, 1);
        const quarterEnd = new Date(selectedYear, selectedQuarter * 3, 0);
        return {
          start: quarterStart,
          end: quarterEnd,
          days: Math.ceil((quarterEnd - quarterStart) / (1000 * 60 * 60 * 24)) + 1
        };
      case 'year':
        const yearStart = new Date(selectedYear, 0, 1);
        const yearEnd = new Date(selectedYear, 11, 31);
        return {
          start: yearStart,
          end: yearEnd,
          days: 365
        };
      case 'custom':
        return {
          start: new Date(dateFrom),
          end: new Date(dateTo),
          days: Math.ceil((new Date(dateTo) - new Date(dateFrom)) / (1000 * 60 * 60 * 24)) + 1
        };
      default:
        return {
          start: new Date(selectedYear, selectedMonth, 1),
          end: new Date(selectedYear, selectedMonth + 1, 0),
          days: new Date(selectedYear, selectedMonth + 1, 0).getDate()
        };
    }
  };

  // Oblicz dostępne tygodnie dla wybranego miesiąca
  const getWeeksInMonth = () => {
    const firstDay = new Date(selectedYear, selectedMonth, 1);
    const lastDay = new Date(selectedYear, selectedMonth + 1, 0);
    const firstWeek = getWeekNumber(firstDay);
    const lastWeek = getWeekNumber(lastDay);
    const weeks = [];
    for (let week = firstWeek; week <= lastWeek; week++) {
      const weekStart = getDateFromWeek(week, selectedYear);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weeks.push({
        number: week,
        start: weekStart,
        end: weekEnd
      });
    }
    return weeks;
  };

  // Aktualizuj dateFrom i dateTo gdy zmienia się viewMode, selectedMonth, selectedYear, selectedWeek, selectedQuarter
  useEffect(() => {
    const range = getDateRange();
    setDateFrom(range.start);
    setDateTo(range.end);
  }, [viewMode, selectedMonth, selectedYear, selectedWeek, selectedQuarter]);

  // Inicjalizuj selectedWeek gdy wybieramy widok "week"
  useEffect(() => {
    if (viewMode === 'week' && selectedWeek === null) {
      const currentWeek = getWeekNumber(new Date());
      setSelectedWeek(currentWeek);
    }
  }, [viewMode]);

  // Wczytaj rotacje dla wybranego projektu (lub wszystkich projektów)
  useEffect(() => {
    if (!selectedProject || selectedProject === 'all') {
      setRotations([]);
      return;
    }

    const loadRotations = async () => {
      try {
        const rotationsData = await rotationsAPI.getByProject(selectedProject);
        const formattedRotations = rotationsData.map(r => ({
          id: r.id.toString(),
          title: r.title,
          rotationNumber: r.rotationNumber,
          pattern: r.pattern || [],
          daysPerCycle: r.daysPerCycle || 9,
          project: r.project || selectedProject
        }));
        setRotations(formattedRotations);
      } catch (error) {
        console.error('Błąd ładowania rotacji:', error);
        setRotations([]);
      }
    };

    loadRotations();
    
    // Nasłuchuj aktualizacji rotacji
    const handleRotationsUpdate = () => {
      loadRotations();
    };
    window.addEventListener('rotationsUpdated', handleRotationsUpdate);
    
    return () => {
      window.removeEventListener('rotationsUpdated', handleRotationsUpdate);
    };
  }, [selectedProject]);

  // Resetuj wybór rotacji gdy zmienia się projekt
  useEffect(() => {
    if (selectedProject) {
      setSelectedRotation('all');
    }
  }, [selectedProject]);

  // Wczytaj dane grafiku dla wybranej rotacji (lub wszystkich rotacji)
  useEffect(() => {
    if (!selectedProject) {
      setScheduleData({});
      return;
    }

    const allScheduleData = {};
    const dateRange = getDateRange();
    
    // Pobierz dane dla wszystkich możliwych miesięcy w zakresie
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    const currentDate = new Date(startDate);
    
    // Lista projektów do przetworzenia
    const projectsToProcess = selectedProject === 'all' ? projects : [selectedProject];
    
    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      
      // Przetwarzaj każdy projekt
      projectsToProcess.forEach(project => {
        // Pobierz rotacje dla tego projektu
        const rotationsKey = `workRotations_${project}`;
        const savedRotations = localStorage.getItem(rotationsKey);
        let projectRotations = [];
        
        if (savedRotations) {
          try {
            projectRotations = JSON.parse(savedRotations);
          } catch (e) {
            console.error('Error parsing rotations:', e);
          }
        }
        
        // Filtruj rotacje jeśli wybrano konkretną
        const rotationsToProcess = selectedRotation === 'all' 
          ? projectRotations 
          : projectRotations.filter(r => String(r.id) === String(selectedRotation));
        
        // Pobierz dane dla wybranych rotacji w tym projekcie i miesiącu
        rotationsToProcess.forEach(rotation => {
          const key = `schedule_${project}_${rotation.id}_${year}_${month}`;
          const savedData = localStorage.getItem(key);
          if (savedData) {
            try {
              const parsed = JSON.parse(savedData);
              // Połącz dane z wybranych rotacji i projektów
              Object.keys(parsed).forEach(dateKey => {
                if (!allScheduleData[dateKey]) {
                  allScheduleData[dateKey] = {};
                }
                Object.assign(allScheduleData[dateKey], parsed[dateKey]);
              });
            } catch (e) {
              console.error('Error parsing schedule data:', e);
            }
          }
        });
      });
      
      // Przejdź do następnego miesiąca
      currentDate.setMonth(currentDate.getMonth() + 1);
      currentDate.setDate(1);
    }

    setScheduleData(allScheduleData);
  }, [selectedProject, selectedRotation, rotations, dateFrom, dateTo, viewMode, selectedMonth, selectedYear, selectedWeek, projects]);

  // Pracownicy przypisani do wybranego projektu i rotacji (lub wszystkich projektów)
  const getProjectEmployees = () => {
    if (!selectedProject) return [];
    
    let filteredUsers = [];
    
    if (selectedProject === 'all') {
      // Zwróć wszystkich pracowników z dostępnych projektów
      filteredUsers = users.filter(u => 
        u.role === 'employee' && 
        u.projects && 
        u.projects.some(project => projects.includes(project))
      );
    } else {
      filteredUsers = users.filter(u => 
        u.projects && u.projects.includes(selectedProject) && u.role === 'employee'
      );
    }
    
    // Jeśli wybrano konkretną rotację, filtruj po niej
    if (selectedRotation !== 'all' && selectedProject !== 'all') {
      filteredUsers = filteredUsers.filter(u => {
        const userRotationId = u.rotationId ? String(u.rotationId) : null;
        return userRotationId === String(selectedRotation);
      });
    }
    
    return filteredUsers;
  };

  const projectEmployees = getProjectEmployees();

  // Oblicz statystyki absencji
  const calculateAbsenceStats = useMemo(() => {
    if (!selectedProject || projectEmployees.length === 0) {
      return {
        byType: {},
        byEmployee: {},
        total: 0,
        dailyStats: []
      };
    }

    const dateRange = getDateRange();
    const statsByType = {};
    const statsByEmployee = {};
    const dailyStats = [];
    let totalAbsences = 0;

    // Inicjalizuj statystyki
    Object.keys(absenceTypes).forEach(type => {
      statsByType[type] = 0;
    });

    projectEmployees.forEach(employee => {
      statsByEmployee[employee.email || employee.id] = {
        name: `${employee.firstName} ${employee.lastName}`,
        absences: {}
      };
      Object.keys(absenceTypes).forEach(type => {
        statsByEmployee[employee.email || employee.id].absences[type] = 0;
      });
    });

    // Formatuj datę jako klucz (YYYY-MM-DD) - zgodnie z formatem w Grafiku
    const formatDateKey = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Przetwarzaj dane z grafiku
    const currentDate = new Date(dateRange.start);
    while (currentDate <= dateRange.end) {
      const dateKey = formatDateKey(currentDate);
      const dayData = scheduleData[dateKey];
      const dayStats = {
        date: new Date(currentDate),
        absences: {}
      };

      Object.keys(absenceTypes).forEach(type => {
        dayStats.absences[type] = 0;
      });

      if (dayData) {
        projectEmployees.forEach(employee => {
          const employeeId = employee.email || employee.id;
          const employeeDayData = dayData[employeeId];
          
          if (employeeDayData) {
            // Sprawdź wszystkie typy absencji (Day, Afternoon, Night)
            const absenceTypes = [
              employeeDayData.absenceTypeDay,
              employeeDayData.absenceTypeAfternoon,
              employeeDayData.absenceTypeNight
            ].filter(type => type && type.trim() !== ''); // Filtruj puste wartości
            
            // Obsługa starego formatu (dla kompatybilności wstecznej)
            if (absenceTypes.length === 0 && employeeDayData.absenceType) {
              absenceTypes.push(employeeDayData.absenceType);
            }
            
            // Przetwórz każdy typ absencji
            absenceTypes.forEach(absenceType => {
              // Pomijaj święta i niedziele w statystykach absencji
              if (absenceType === 'swieto' || absenceType === 'niedziela') {
                return;
              }
              statsByType[absenceType] = (statsByType[absenceType] || 0) + 1;
              statsByEmployee[employeeId].absences[absenceType] = 
                (statsByEmployee[employeeId].absences[absenceType] || 0) + 1;
              dayStats.absences[absenceType] = (dayStats.absences[absenceType] || 0) + 1;
              totalAbsences++;
            });
          }
        });
      }

      dailyStats.push(dayStats);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return {
      byType: statsByType,
      byEmployee: statsByEmployee,
      total: totalAbsences,
      dailyStats: dailyStats
    };
  }, [selectedProject, projectEmployees, scheduleData, viewMode, dateFrom, dateTo, selectedMonth, selectedYear, selectedWeek, selectedQuarter]);

  // Przygotuj dane do wykresów (pomijaj święta i niedziele)
  const chartDataByType = Object.entries(calculateAbsenceStats.byType)
    .filter(([type, count]) => count > 0 && type !== 'swieto' && type !== 'niedziela')
    .map(([type, count]) => ({
      name: absenceTypes[type]?.label || type,
      value: count,
      color: absenceTypes[type]?.color || '#6b7280'
    }));

  const chartDataByEmployee = Object.entries(calculateAbsenceStats.byEmployee)
    .map(([employeeId, data]) => {
      // Oblicz sumę pomijając święta i niedziele
      const total = Object.entries(data.absences)
        .filter(([type]) => type !== 'swieto' && type !== 'niedziela')
        .reduce((sum, [, count]) => sum + count, 0);
      return {
        name: data.name,
        total: total,
        absences: data.absences
      };
    })
    .filter(item => item.total > 0)
    .sort((a, b) => b.total - a.total);

  const dailyChartData = calculateAbsenceStats.dailyStats.map(day => ({
    date: `${day.date.getDate()}.${day.date.getMonth() + 1}`,
    ...day.absences
  }));

  // Oblicz sumy dla każdego typu (pomijaj święta i niedziele)
  const totalByType = Object.entries(calculateAbsenceStats.byType)
    .filter(([type, count]) => count > 0 && type !== 'swieto' && type !== 'niedziela')
    .map(([type, count]) => ({
      type,
      label: absenceTypes[type]?.label || type,
      count,
      color: absenceTypes[type]?.color || '#6b7280',
      icon: absenceTypes[type]?.icon || '📅'
    }));

  return (
    <div className="absencje-container">
      <div className="absencje-header">
        <h3>Absencje</h3>
        <div className="absencje-controls">
          {projects.length > 0 && (
            <div className="project-selector">
              <label htmlFor="project-select-absencje">Klient:</label>
              <select
                id="project-select-absencje"
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="project-select"
              >
                <option value="all">Wszystkie klienty</option>
                {projects.map((project, idx) => (
                  <option key={idx} value={project}>{project}</option>
                ))}
              </select>
            </div>
          )}
          {selectedProject && selectedProject !== 'all' && rotations.length > 0 && (
            <div className="rotation-selector">
              <label htmlFor="rotation-select-absencje">Zmiana:</label>
              <select
                id="rotation-select-absencje"
                value={selectedRotation}
                onChange={(e) => setSelectedRotation(e.target.value)}
                className="rotation-select"
              >
                <option value="all">Wszystkie zmiany</option>
                {rotations.map((rotation) => (
                  <option key={rotation.id} value={rotation.id}>
                    {rotation.title || rotation.name || `Zmiana ${rotation.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="view-mode-selector">
            <label htmlFor="view-mode-select">Widok:</label>
            <select
              id="view-mode-select"
              value={viewMode}
              onChange={(e) => {
                setViewMode(e.target.value);
                if (e.target.value === 'month') {
                  setSelectedMonth(new Date().getMonth());
                  setSelectedYear(new Date().getFullYear());
                } else if (e.target.value === 'week') {
                  setSelectedWeek(getWeekNumber(new Date()));
                  setSelectedMonth(new Date().getMonth());
                  setSelectedYear(new Date().getFullYear());
                } else if (e.target.value === 'quarter') {
                  setSelectedQuarter(Math.floor(new Date().getMonth() / 3) + 1);
                  setSelectedYear(new Date().getFullYear());
                }
              }}
              className="view-mode-select"
            >
              <option value="day">Dzień</option>
              <option value="week">Tydzień</option>
              <option value="month">Miesiąc</option>
              <option value="quarter">Kwartał</option>
              <option value="year">Rok</option>
              <option value="custom">Niestandardowy (OD-DO)</option>
            </select>
          </div>
          
          {viewMode === 'custom' && (
            <>
              <div className="date-selector">
                <label htmlFor="date-from-select">Data od:</label>
                <input
                  type="date"
                  id="date-from-select"
                  value={dateFrom.toISOString().split('T')[0]}
                  onChange={(e) => setDateFrom(new Date(e.target.value))}
                  className="date-input"
                />
              </div>
              <div className="date-selector">
                <label htmlFor="date-to-select">Data do:</label>
                <input
                  type="date"
                  id="date-to-select"
                  value={dateTo.toISOString().split('T')[0]}
                  onChange={(e) => setDateTo(new Date(e.target.value))}
                  className="date-input"
                />
              </div>
            </>
          )}

          {viewMode === 'month' && (
            <>
              <div className="month-selector">
                <label htmlFor="month-select">Miesiąc:</label>
                <select
                  id="month-select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="month-select"
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>{month}</option>
                  ))}
                </select>
              </div>
              <div className="year-selector">
                <label htmlFor="year-select">Rok:</label>
                <input
                  type="number"
                  id="year-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  min="2020"
                  max="2100"
                  className="year-input"
                />
              </div>
            </>
          )}

          {viewMode === 'week' && (
            <>
              <div className="month-selector">
                <label htmlFor="month-select-week">Miesiąc:</label>
                <select
                  id="month-select-week"
                  value={selectedMonth}
                  onChange={(e) => {
                    const newMonth = parseInt(e.target.value);
                    setSelectedMonth(newMonth);
                    // Ustaw pierwszy tydzień miesiąca jako domyślny
                    const firstDay = new Date(selectedYear, newMonth, 1);
                    setSelectedWeek(getWeekNumber(firstDay));
                  }}
                  className="month-select"
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>{month}</option>
                  ))}
                </select>
              </div>
              <div className="year-selector">
                <label htmlFor="year-select-week">Rok:</label>
                <input
                  type="number"
                  id="year-select-week"
                  value={selectedYear}
                  onChange={(e) => {
                    const newYear = parseInt(e.target.value);
                    setSelectedYear(newYear);
                    // Ustaw pierwszy tydzień miesiąca jako domyślny
                    const firstDay = new Date(newYear, selectedMonth, 1);
                    setSelectedWeek(getWeekNumber(firstDay));
                  }}
                  min="2020"
                  max="2100"
                  className="year-input"
                />
              </div>
              <div className="week-selector">
                <label htmlFor="week-select">Tydzień:</label>
                <select
                  id="week-select"
                  value={selectedWeek || ''}
                  onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                  className="week-select"
                >
                  {getWeeksInMonth().map((week) => (
                    <option key={week.number} value={week.number}>
                      Tydzień {week.number} ({week.start.getDate()}.{week.start.getMonth() + 1} - {week.end.getDate()}.{week.end.getMonth() + 1})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {viewMode === 'day' && (
            <div className="date-selector">
              <label htmlFor="date-select">Data:</label>
              <input
                type="date"
                id="date-select"
                value={dateFrom.toISOString().split('T')[0]}
                onChange={(e) => setDateFrom(new Date(e.target.value))}
                className="date-input"
              />
            </div>
          )}

          {viewMode === 'quarter' && (
            <>
              <div className="quarter-selector">
                <label htmlFor="quarter-select">Kwartał:</label>
                <select
                  id="quarter-select"
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
                  className="quarter-select"
                >
                  <option value="1">Q1 (I kwartał - Styczeń-Marzec)</option>
                  <option value="2">Q2 (II kwartał - Kwiecień-Czerwiec)</option>
                  <option value="3">Q3 (III kwartał - Lipiec-Wrzesień)</option>
                  <option value="4">Q4 (IV kwartał - Październik-Grudzień)</option>
                </select>
              </div>
              <div className="year-selector">
                <label htmlFor="year-select-quarter">Rok:</label>
                <input
                  type="number"
                  id="year-select-quarter"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  min="2020"
                  max="2100"
                  className="year-input"
                />
              </div>
            </>
          )}

          {viewMode === 'year' && (
            <div className="year-selector">
              <label htmlFor="year-select-range">Rok:</label>
              <input
                type="number"
                id="year-select-range"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                min="2020"
                max="2100"
                className="year-input"
              />
            </div>
          )}
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="no-projects-message">
          <p>Nie masz przypisanych projektów. Skontaktuj się z administratorem.</p>
        </div>
      ) : (
        <div className="absencje-content">
          {selectedProject && (
            <>
              {/* Informacja o wybranym projekcie */}
              {selectedProject === 'all' && (
                <div className="project-info-banner">
                  <span className="info-icon">ℹ️</span>
                  <span>Wyświetlane są dane ze wszystkich klientów: {projects.join(', ')}</span>
                </div>
              )}
              
              {/* Statystyki ogólne */}
              <div className="stats-overview">
                <div className="stat-card">
                  <div className="stat-icon">👥</div>
                  <div className="stat-value">{projectEmployees.length}</div>
                  <div className="stat-label">Pracowników</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">📅</div>
                  <div className="stat-value">{calculateAbsenceStats.total}</div>
                  <div className="stat-label">Dni absencji</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">📊</div>
                  <div className="stat-value">
                    {projectEmployees.length > 0 
                      ? ((calculateAbsenceStats.total / (projectEmployees.length * getDateRange().days)) * 100).toFixed(1)
                      : 0}%
                  </div>
                  <div className="stat-label">Wskaźnik absencji</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🏖️</div>
                  <div className="stat-value">{totalByType.length}</div>
                  <div className="stat-label">Typy absencji</div>
                </div>
              </div>

              {/* Wykresy */}
              <div className="charts-section">
                <div className="chart-container">
                  <h4>Absencje według typu</h4>
                  {chartDataByType.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={chartDataByType}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {chartDataByType.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="no-data-message">Brak danych do wyświetlenia</div>
                  )}
                </div>

                <div className="chart-container">
                  <h4>Absencje według pracownika</h4>
                  {chartDataByEmployee.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartDataByEmployee}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="total" fill="#667eea" name="Łączna liczba dni" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="no-data-message">Brak danych do wyświetlenia</div>
                  )}
                </div>

                {viewMode !== 'day' && (
                  <div className="chart-container full-width">
                    <h4>Absencje w czasie</h4>
                    {dailyChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={dailyChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          {Object.keys(absenceTypes)
                            .filter(type => type !== 'swieto' && type !== 'niedziela')
                            .map((type, index) => {
                              const typeData = dailyChartData.some(d => d[type] > 0);
                              if (!typeData) return null;
                              return (
                                <Line
                                  key={type}
                                  type="monotone"
                                  dataKey={type}
                                  stroke={absenceTypes[type].color}
                                  name={absenceTypes[type].label}
                                  strokeWidth={2}
                                />
                              );
                            })}
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="no-data-message">Brak danych do wyświetlenia</div>
                    )}
                  </div>
                )}
              </div>

              {/* Szczegółowa tabela */}
              <div className="detailed-stats">
                <h4>Szczegółowe statystyki</h4>
                <div className="stats-by-type">
                  <h5>Według typu absencji</h5>
                  <div className="type-stats-grid">
                    {totalByType.map((item, index) => (
                      <div key={index} className="type-stat-card" style={{ borderLeftColor: item.color }}>
                        <div className="type-stat-icon">{item.icon}</div>
                        <div className="type-stat-info">
                          <div className="type-stat-label">{item.label}</div>
                          <div className="type-stat-value">{item.count} dni</div>
                        </div>
                      </div>
                    ))}
                    {totalByType.length === 0 && (
                      <div className="no-data-message">Brak absencji w wybranym okresie</div>
                    )}
                  </div>
                </div>

                <div className="stats-by-employee">
                  <h5>Według pracownika</h5>
                  {chartDataByEmployee.length > 0 ? (
                    <div className="employee-stats-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Pracownik</th>
                            {Object.keys(absenceTypes)
                              .filter(type => type !== 'swieto' && type !== 'niedziela')
                              .map(type => {
                                const hasData = chartDataByEmployee.some(emp => emp.absences[type] > 0);
                                if (!hasData) return null;
                                return (
                                  <th key={type} style={{ color: absenceTypes[type].color }}>
                                    {absenceTypes[type].icon} {absenceTypes[type].label}
                                  </th>
                                );
                              })}
                            <th>Suma</th>
                          </tr>
                        </thead>
                        <tbody>
                          {chartDataByEmployee.map((employee, index) => (
                            <tr key={index}>
                              <td><strong>{employee.name}</strong></td>
                              {Object.keys(absenceTypes)
                                .filter(type => type !== 'swieto' && type !== 'niedziela')
                                .map(type => {
                                  const hasData = chartDataByEmployee.some(emp => emp.absences[type] > 0);
                                  if (!hasData) return null;
                                  return (
                                    <td key={type}>{employee.absences[type] || 0}</td>
                                  );
                                })}
                              <td><strong>{employee.total}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-data-message">Brak absencji w wybranym okresie</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Absencje;
